import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Widget {
  id: number;
  status: string;
  details: string;
  persondetails: string;
  isChecked: boolean;
}

// ─── Edit Person Details Modal ────────────────────────────────────────────────

interface EditModalProps {
  widget: Widget;
  allPersonTypes: string[];
  onSave: (updated: Widget) => void;
  onClose: () => void;
}

const EditPersonDetailsModal: React.FC<EditModalProps> = ({
  widget,
  allPersonTypes,
  onSave,
  onClose,
}) =>
{
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        widget.persondetails
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
      ),
  );
  const [saving, setSaving] = useState(false);

  const filtered = allPersonTypes.filter(p => p.toLowerCase().includes(filter.toLowerCase()));

  const toggle = (name: string) =>
  {
    setSelected(prev =>
    {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleSave = async () =>
  {
    setSaving(true);
    try
    {
      const updated: Widget = {
        ...widget,
        persondetails: Array.from(selected).join(','),
      };
      const result = await adminConsoleService.updateHomeSettingsWidget(updated);
      onSave(result);
      onClose();
    }
    catch
    {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[420px] max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0] flex-shrink-0">
          <h2 className="text-[16px] font-normal text-[#5697b3]">Edit Person Details</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 pb-2 flex-shrink-0">
          <div className="relative">
            <span className="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[15px]">
              search
            </span>
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Find by keyword"
              className="w-full pl-8 pr-3 py-1.5 border border-[#ddd] rounded text-[12px] outline-none focus:border-[#62aacb]"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 pb-2">
          <p className="text-[11px] text-[#888] uppercase tracking-wider font-medium mb-2">
            Person Details
          </p>
          {filtered.map(name => (
            <div
              key={name}
              className="flex items-center gap-2.5 py-1.5 hover:bg-[#f5f5f5] rounded px-1"
            >
              <span className="corgee_com_checkbox flex-shrink-0">
                <input
                  id={`lpd-${name.replace(/\s+/g, '-')}`}
                  type="checkbox"
                  checked={selected.has(name)}
                  onChange={() => toggle(name)}
                />
                <label htmlFor={`lpd-${name.replace(/\s+/g, '-')}`}></label>
              </span>
              <label
                htmlFor={`lpd-${name.replace(/\s+/g, '-')}`}
                className="text-[12px] text-[#333] cursor-pointer select-none"
              >
                {name}
              </label>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-[12px] text-[#aaa] italic py-2">No results.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#e0e0e0] px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-[12px] border border-[#ddd] rounded text-[#555] hover:bg-[#f5f5f5] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const LinkToCustomDetailsTab: React.FC = () =>
{
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [personTypes, setPersonTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [editTarget, setEditTarget] = useState<Widget | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() =>
  {
    Promise.all([
      adminConsoleService.getHomeSettingsWidget(),
      adminConsoleService.getPersonTypeWidget(),
    ])
      .then(([w, p]) =>
      {
        setWidgets(w);
        setPersonTypes(p);
      })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = widgets.filter(
    w =>
      w.status.toLowerCase().includes(filter.toLowerCase()) ||
      w.details.toLowerCase().includes(filter.toLowerCase()) ||
      w.persondetails.toLowerCase().includes(filter.toLowerCase()),
  );

  const handleToggle = useCallback(async (widget: Widget) =>
  {
    const updated: Widget = { ...widget, isChecked: !widget.isChecked };
    setWidgets(prev => prev.map(w => (w.id === widget.id ? updated : w)));
    setTogglingId(widget.id);
    try
    {
      const result = await adminConsoleService.updateHomeSettingsWidget(updated);
      setWidgets(prev => prev.map(w => (w.id === result.id ? result : w)));
    }
    catch
    {
      // Rollback
      setWidgets(prev => prev.map(w => (w.id === widget.id ? widget : w)));
      setError('Failed to update widget.');
    }
    finally
    {
      setTogglingId(null);
    }
  }, []);

  const handleSavePersonDetails = useCallback((updated: Widget) =>
  {
    setWidgets(prev => prev.map(w => (w.id === updated.id ? updated : w)));
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="flex-shrink-0 px-6 pt-5 pb-3">
        <h3 className="text-[16px] font-normal text-[#5697b3] mb-2">Link to Custom Details</h3>
        <p className="text-[12px] text-[#888] leading-relaxed max-w-[680px]">
          Add widgets to the Home view by linking a Status detail to a Person detail. Click on Edit
          to select one or more Person details that you want to link with each Status. Then click on
          the checkbox to enable it. The widget will be displayed for all users in Home view with
          results according to their assignments and permissions.
        </p>
      </div>

      {/* ── Toolbar ── */}
      <div className="flex-shrink-0 px-6 pb-3">
        <div className="relative w-[220px]">
          <span className="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[16px]">
            search
          </span>
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Find by keyword"
            className="w-full pl-8 pr-3 py-1.5 border border-[#ddd] rounded text-[12px] outline-none focus:border-[#62aacb]"
          />
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mx-6 mb-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)}>
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px] w-[24px]">link_off</span>
            {filter ? 'No results match your search.' : 'No widgets configured.'}
          </div>
        ) : (
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[#e0e0e0] text-[#888]">
                <th className="text-left py-2 px-3 font-medium w-[20%]">Status</th>
                <th className="text-left py-2 px-3 font-medium w-[20%]">Details</th>
                <th className="text-left py-2 px-3 font-medium w-[50%]">Person details</th>
                <th className="py-2 px-3 w-[10%]" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((widget, index) => (
                <tr
                  key={index}
                  className="border-b border-[#f0f0f0] hover:bg-[#fafafa] transition-colors"
                >
                  <td className="py-2 px-3 text-[#333]">{widget.status.trim()}</td>
                  <td className="py-2 px-3 text-[#333]">{widget.details}</td>
                  <td className="py-2 px-3 text-[#666]">{widget.persondetails}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-2">
                      {/* Toggle checkbox */}
                      <span
                        className={`corgee_com_checkbox${togglingId === widget.id ? ' opacity-50 pointer-events-none' : ''}`}
                      >
                        <input
                          id={`lct-toggle-${widget.id}`}
                          type="checkbox"
                          checked={widget.isChecked}
                          onChange={() => handleToggle(widget)}
                          disabled={togglingId === widget.id}
                        />
                        <label htmlFor={`lct-toggle-${widget.id}`}></label>
                      </span>
                      {/* Edit */}
                      <button
                        onClick={() => setEditTarget(widget)}
                        className="text-[#bbb] hover:text-[#62aacb] transition-colors"
                        title="Edit person details"
                      >
                        <span className="material-icons text-[16px]">edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Edit Modal ── */}
      {editTarget && (
        <EditPersonDetailsModal
          widget={editTarget}
          allPersonTypes={personTypes}
          onSave={handleSavePersonDetails}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
};
